/**
 * The only place that talks to the API.
 *
 * Cookies do the authenticating, so every call sends credentials and
 * echoes the CSRF cookie back in a header. Nothing here stores a
 * token: the session cookie is HttpOnly and unreachable from JS,
 * which is the point of putting it there instead of localStorage.
 */
const BASE = import.meta.env.DEV ? '/api' : `https://api.${location.hostname.split('.').slice(-2).join('.')}`;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)munin_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (init.method && init.method !== 'GET') headers.set('X-CSRF-Token', csrfToken());

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'include' });
  } catch {
    // A network failure is not a 500 and must not be reported as one.
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const e = body?.error ?? {};
    const full = String(e.message ?? 'Something went wrong.');
    // In development the API returns the real error, which can be a
    // multi-line stack trace. Rendering that verbatim into a banner
    // destroys the layout and tells the user nothing, so the full
    // text goes to the console and the UI gets a readable slice.
    if (full.length > 240) console.error('API error:', full);
    const shown = full.length > 240 ? full.slice(0, 240).trimEnd() + '…' : full;
    throw new ApiError(res.status, e.code ?? 'error', shown, e.details);
  }
  return body as T;
}

export const api = {
  get: <T,>(p: string) => request<T>(p),
  post: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),

  put: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),

  del: <T,>(p: string) => request<T>(p, { method: 'DELETE' }),

  /**
   * Raw-body upload of a programme file. Same shape as file upload:
   * the browser posts the File directly, so there is no multipart
   * parser and no temporary file anywhere in the path.
   */
  uploadProgramme: (query: string, text: string) =>
    request<{
      versionId: string;
      activities: number;
      warnings: string[];
      diff: { baseline: boolean; summary: string; counts: Record<string, number> | null };
    }>(`/programme/versions?${query}`, {
      method: 'POST',
      body: text,
      headers: { 'Content-Type': 'text/plain' },
    }),

  /** Raw-body upload — see apps/api/src/routes/files.ts for why. */
  upload: (file: File) =>
    request<{ file: { id: string; originalName: string; size: number } }>('/files', {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': file.name },
    }),

  fileUrl: (id: string) => `${BASE}/files/${id}`,

  /**
   * URL absolut ke API, untuk unduhan yang dibuka sebagai navigasi
   * biasa. Cookie sesi bercakupan .muninpartners.com, jadi ia ikut
   * terkirim tanpa perlu fetch dan blob.
   */
  url: (path: string) => `${BASE}${path}`,
};

/* ---------- shapes returned by the API ---------- */

export type Me = {
  user: { id: string; email: string; name: string };
  company: { id: string; name: string };
  roles: Record<string, string>;
};

export type DocRow = {
  id: string;
  docNumber: string;
  title: string;
  kind: 'DOCUMENT' | 'DELIVERABLE';
  state: string;
  currentStage: string | null;
  currentRevCode: string | null;
  docType: { code: string; name: string };
  discipline: { code: string; name: string } | null;
  project: { id: string; code: string };
};

export type Action = {
  id: string;
  code: string;
  comment: string;
  actedAt: string;
  actor: { id: string; name: string };
};

export type Step = {
  id: string;
  level: number;
  role: string;
  state: 'NOT_STARTED' | 'AWAITING' | 'DONE' | 'SKIPPED';
  quorum: number;
  dueAt: string | null;
  resolvedCode: string | null;
  discipline: { code: string; name: string } | null;
  assignee: { id: string; name: string } | null;
  actions: Action[];
};

export type Revision = {
  id: string;
  revCode: string;
  revIndex: number;
  stage: string | null;
  state: string;
  submittedAt: string | null;
  submittedBy: { id: string; name: string };
  fileId: string | null;
  steps: Step[];
};

export type InboxItem = {
  stepId: string;
  level: number;
  role: string;
  dueAt: string | null;
  overdue: boolean;
  discipline: { code: string; name: string } | null;
  revision: { id: string; revCode: string; stage: string | null };
  document: { id: string; docNumber: string; title: string; kind: string };
};
